import React, {
  ComponentProps,
  Fragment,
  useEffect,
  useId,
  useRef,
} from 'react'

import { Listbox } from '@headlessui/react'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import classnames from 'classnames'

import { CheckIcon } from '../icons'
import { Label } from './Label'

export const SelectV2 = (
  props: ({ value: string } | { defaultValue: string }) & {
    name: string
    options: { value: string; label: string }[]
    placeholder?: string
    fullWidth?: boolean
    triggerOpen?: boolean
    onChange?: (v: string) => void
    label?: React.ReactNode
    labelProps?: ComponentProps<'label'>
    required?: boolean
    error?: string | string[]
    disabled?: boolean
    width?: number
    bgFunctionCV2?: boolean
    rightIcon?: React.ReactNode
  }
) => {
  const id = useId()
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (props.triggerOpen) {
      buttonRef.current?.click()
    }
  }, [props.triggerOpen])

  return (
    <div
      className={classnames({
        'w-full': props.fullWidth,
      })}
      style={{ width: `${props.width}px` }}
    >
      {props.label && (
        <Label
          label={props.label}
          labelProps={{ ...props.labelProps, htmlFor: id }}
          required={props.required}
        />
      )}
      <Listbox
        disabled={props.disabled}
        name={props.name}
        defaultValue={'defaultValue' in props ? props.defaultValue : undefined}
        value={'value' in props ? props.value : undefined}
        onChange={(v) => {
          props.onChange?.(v)
        }}
      >
        <Listbox.Button as={Fragment}>
          {({ value }) => {
            return (
              <button
                type="button"
                ref={buttonRef}
                className={classnames(
                  'inline-flex items-center justify-between rounded px-3 text-sm leading-none h-10 gap-3 border border-gray outline-none w-full',
                  {
                    'bg-white': !props.bgFunctionCV2 && !props.disabled,
                    'bg-gray': props.disabled,
                    'bg-function-cv2': props.bgFunctionCV2,
                  }
                )}
              >
                <div className="truncate">
                  <div>
                    {props.options.find((opt) => opt.value === value)?.label ??
                      props.placeholder}
                  </div>
                </div>
                {props.rightIcon ?? <ChevronDownIcon fill="#666" />}
              </button>
            )
          }}
        </Listbox.Button>
        <Listbox.Options className="z-[999] overflow-scroll bg-white rounded-md shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] absolute max-h-[12em]">
          {props.options.map((option) => {
            return (
              <div
                key={option.value}
                className="text-sm leading-none flex items-center relative select-none  data-[disabled]:pointer-events-none outline-none w-full border-b-[1px] border-[#efefef] last:border-0"
              >
                <Listbox.Option value={option.value}>
                  {({ selected }) => (
                    <div className="flex gap-x-1 items-center relative px-3 h-10">
                      {selected && (
                        <CheckIcon className="w-4 h-4 fill-text absolute left-2" />
                      )}
                      <span className="ml-4">{option.label}</span>
                    </div>
                  )}
                </Listbox.Option>
              </div>
            )
          })}
        </Listbox.Options>
      </Listbox>

      {props.error && (
        <div className="text-sm text-[#C60017] mt-1">
          {typeof props.error === 'string' ? props.error : props.error[0]}
        </div>
      )}
    </div>
  )
}
